/**
 * Utility functions for monitoring and optimizing application performance
 */

// Default performance markers
export const PERFORMANCE_MARKERS = {
  APP_INIT: 'app_initialization',
  DATA_LOAD_START: 'data_load_start',
  DATA_LOAD_END: 'data_load_end',
  RENDER_START: 'render_start',
  RENDER_END: 'render_end',
  PROCESSING_START: 'processing_start',
  PROCESSING_END: 'processing_end'
};

// Performance metrics storage
const performanceMetrics = {
  markers: {},
  measures: {},
  resources: []
};

/**
 * Mark the beginning of a performance-sensitive operation
 * 
 * @param {string} markerId - Unique identifier for this marker
 * @param {string} description - Optional description of what's being measured
 */
export const markStart = (markerId, description = '') => {
  if (typeof window === 'undefined' || !window.performance) return;

  const marker = `${markerId}_start`;
  const timestamp = performance.now();
  
  // Store marker data
  performanceMetrics.markers[marker] = {
    id: marker,
    timestamp,
    description,
    type: 'start'
  };
  
  // Use the Performance API if available
  try {
    performance.mark(marker);
  } catch (e) {
    console.warn('Performance API mark failed:', e);
  }
  
  return timestamp;
};

/**
 * Mark the end of a performance-sensitive operation and measure the duration
 * 
 * @param {string} markerId - Unique identifier matching a previous markStart call
 * @param {boolean} logToConsole - Whether to log the measurement to console
 * @returns {number} - Duration in milliseconds
 */
export const markEnd = (markerId, logToConsole = false) => {
  if (typeof window === 'undefined' || !window.performance) return 0;
  
  const startMarker = `${markerId}_start`;
  const endMarker = `${markerId}_end`;
  const timestamp = performance.now();
  
  // Store marker data
  performanceMetrics.markers[endMarker] = {
    id: endMarker,
    timestamp,
    type: 'end'
  };
  
  // Use the Performance API if available
  try {
    performance.mark(endMarker);
    performance.measure(markerId, startMarker, endMarker);
    
    // Get the duration
    const measures = performance.getEntriesByName(markerId, 'measure');
    const duration = measures.length > 0 ? measures[0].duration : 0;
    
    // Store the measurement
    performanceMetrics.measures[markerId] = {
      id: markerId,
      duration,
      startTime: performanceMetrics.markers[startMarker]?.timestamp || 0,
      endTime: timestamp
    };
    
    if (logToConsole) {
      console.log(`⏱️ Performance [${markerId}]: ${duration.toFixed(2)}ms`);
    }
    
    return duration;
  } catch (e) {
    console.warn('Performance API measure failed:', e);
    
    // Fallback to calculated duration
    const startTime = performanceMetrics.markers[startMarker]?.timestamp || 0;
    const duration = startTime > 0 ? timestamp - startTime : 0;
    
    // Store the measurement
    performanceMetrics.measures[markerId] = {
      id: markerId,
      duration,
      startTime,
      endTime: timestamp
    };
    
    if (logToConsole) {
      console.log(`⏱️ Performance [${markerId}]: ${duration.toFixed(2)}ms (fallback calculation)`);
    }
    
    return duration;
  }
};

/**
 * Measure the execution time of a function
 * 
 * @param {Function} fn - Function to measure
 * @param {string} name - Name for the measurement
 * @param {boolean} logToConsole - Whether to log to console
 * @returns {*} - The result of the function
 */
export const measureFunction = (fn, name = 'function_execution', logToConsole = false) => {
  if (typeof fn !== 'function') {
    throw new Error('First argument must be a function');
  }
  
  markStart(name);
  const result = fn();
  markEnd(name, logToConsole);
  
  return result;
};

/**
 * Measure the execution time of an async function
 * 
 * @param {Function} asyncFn - Async function to measure
 * @param {string} name - Name for the measurement
 * @param {boolean} logToConsole - Whether to log to console
 * @returns {Promise<*>} - The result of the async function
 */
export const measureAsyncFunction = async (asyncFn, name = 'async_function_execution', logToConsole = false) => {
  if (typeof asyncFn !== 'function') {
    throw new Error('First argument must be a function');
  }
  
  markStart(name);
  try {
    const result = await asyncFn();
    markEnd(name, logToConsole);
    return result;
  } catch (error) {
    markEnd(name, logToConsole);
    throw error;
  }
};

/**
 * Throttle a function to prevent excessive calls
 * 
 * @param {Function} fn - Function to throttle
 * @param {number} delay - Throttle delay in milliseconds
 * @returns {Function} - Throttled function
 */
export const throttle = (fn, delay = 300) => {
  let lastCall = 0;
  let lastCallTimer = null;
  
  return function(...args) {
    const now = Date.now();
    const remaining = delay - (now - lastCall);
    
    if (remaining <= 0) {
      lastCall = now;
      return fn.apply(this, args);
    } else {
      clearTimeout(lastCallTimer);
      lastCallTimer = setTimeout(() => {
        lastCall = Date.now();
        fn.apply(this, args);
      }, remaining);
      return lastCallTimer;
    }
  };
};

/**
 * Debounce a function to wait until after a specified delay 
 * to execute after the last call
 * 
 * @param {Function} fn - Function to debounce
 * @param {number} delay - Debounce delay in milliseconds
 * @returns {Function} - Debounced function
 */
export const debounce = (fn, delay = 300) => {
  let timeout;
  
  return function(...args) {
    clearTimeout(timeout);
    
    timeout = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
    
    return timeout;
  };
};

/**
 * Get all collected performance metrics
 * 
 * @returns {Object} - All performance metrics
 */
export const getPerformanceMetrics = () => {
  return { ...performanceMetrics };
};

/**
 * Clear all collected performance metrics
 */
export const clearPerformanceMetrics = () => {
  performanceMetrics.markers = {};
  performanceMetrics.measures = {};
  performanceMetrics.resources = [];
  
  // Clear Performance API entries if available
  if (typeof window !== 'undefined' && window.performance && performance.clearMarks) {
    performance.clearMarks();
    performance.clearMeasures();
  }
};

/**
 * Detect long-running tasks that might block the UI
 * 
 * @param {Function} taskFn - The task to run
 * @param {number} threshold - Time threshold in ms to consider a task as long-running
 * @param {Function} onLongTask - Callback when a long-running task is detected
 * @returns {*} - The result of the task
 */
export const detectLongTask = (taskFn, threshold = 50, onLongTask = null) => {
  const taskName = taskFn.name || 'anonymous_task';
  const startTime = performance.now();
  
  const result = taskFn();
  
  const duration = performance.now() - startTime;
  if (duration > threshold) {
    const message = `Long-running task detected: ${taskName} took ${duration.toFixed(2)}ms`;
    console.warn(message);
    
    if (onLongTask && typeof onLongTask === 'function') {
      onLongTask({
        taskName,
        duration,
        threshold
      });
    }
  }
  
  return result;
};

/**
 * Create a higher-order component that monitors render performance
 * 
 * @param {string} componentName - Name of the component to monitor
 * @param {number} threshold - Time threshold in ms to log slow renders
 * @returns {Function} - HOC function to wrap a component
 */
export const withRenderPerformance = (componentName, threshold = 16) => {
  return (WrappedComponent) => {
    return function WithRenderPerformance(props) {
      const markerId = `render_${componentName}`;
      markStart(markerId);
      
      // Save reference to the rendered component
      const result = <WrappedComponent {...props} />;
      
      // Use setTimeout to measure the render time after component is mounted/updated
      setTimeout(() => {
        const duration = markEnd(markerId);
        
        if (duration > threshold) {
          console.warn(`Slow render detected in ${componentName}: ${duration.toFixed(2)}ms`);
        }
      }, 0);
      
      return result;
    };
  };
};

export default {
  markStart,
  markEnd,
  measureFunction,
  measureAsyncFunction,
  throttle,
  debounce,
  getPerformanceMetrics,
  clearPerformanceMetrics,
  detectLongTask,
  withRenderPerformance,
  PERFORMANCE_MARKERS
}; 